import React, { Fragment, useState, useEffect } from "react";
import { Grid, Stack, Typography, Divider, Box } from "@mui/material";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
} from "@mui/material";
import { ContainerCard, DateTextField } from "../../components";
import LoaderCard from "../../components/cards/LoaderCard";
import NoDataCard from "../../components/cards/NoDataCard";
import { api, urls } from "../../services";
import { useAuth, useToast } from "../../contexts";
import moment from "moment";
import dayjs from "dayjs";

export function Purchases(props) {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const { logout } = useAuth();

  const getPayments = () => {
    setIsLoading(true);
    const params = { page: page + 1, limit: rowsPerPage };
    if (fromDate) params.from_date = moment(fromDate).format("YYYY-MM-DD");
    if (toDate) params.to_date = moment(toDate).format("YYYY-MM-DD");
    api
      .get(urls.paymentLogs, { params })
      .then((res) => {
        setPayments(res.data.data.payments);
        setSummary(res.data.data.summary);
        setTotal(res.data.data.pagination.total);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if (err.response?.status === 401) {
          logout();
        } else {
          showToast(err.response?.data?.message, "error");
        }
      });
  };

  useEffect(() => {
    getPayments();
  }, [page, rowsPerPage, fromDate, toDate]);

  const handleFromDateChange = (date) => {
    setPage(0);
    setFromDate(date ?? null);
  };

  const handleToDateChange = (date) => {
    setPage(0);
    setToDate(date ?? null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Fragment>
      <Grid container spacing={2} direction="column">
        <Grid item>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
              Purchases
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#757575" }}>
              {summary
                ? `${summary.count} successful · ₹${summary.total_amount} (selected range)`
                : ""}
            </Typography>
          </Stack>
        </Grid>
        <Grid item>
          <ContainerCard>
            <Stack spacing={2} direction="row" sx={{ mb: 2 }}>
              <Box sx={{ width: 220 }}>
                <DateTextField
                  label="From Date"
                  value={fromDate === null ? null : dayjs(fromDate)}
                  onChange={handleFromDateChange}
                />
              </Box>
              <Box sx={{ width: 220 }}>
                <DateTextField
                  label="To Date"
                  value={toDate === null ? null : dayjs(toDate)}
                  onChange={handleToDateChange}
                />
              </Box>
            </Stack>
            {isLoading && <LoaderCard />}
            {!isLoading && (
              <Fragment>
                {payments.length === 0 && (
                  <NoDataCard title={"No Purchases Found"} />
                )}
                {payments.length > 0 && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                            Date
                          </TableCell>
                          <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                            Name
                          </TableCell>
                          <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                            Phone
                          </TableCell>
                          <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                            Amount
                          </TableCell>
                          <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                            Method
                          </TableCell>
                          <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                            Type
                          </TableCell>
                          <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                            Plan
                          </TableCell>
                          <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                            Window
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {payments.map((p, i) => (
                          <TableRow key={i} hover>
                            <TableCell>
                              {p.created_at
                                ? moment(p.created_at).format("DD MMM YYYY, HH:mm")
                                : "-"}
                            </TableCell>
                            <TableCell>{p.name ?? "-"}</TableCell>
                            <TableCell>{p.phone ?? "-"}</TableCell>
                            <TableCell>{`₹${p.amount ?? "-"}`}</TableCell>
                            <TableCell>{p.payment_method ?? "-"}</TableCell>
                            <TableCell>
                              {p.payment_type === "direct"
                                ? "Direct (App/Web)"
                                : "Payment Link"}
                            </TableCell>
                            <TableCell>{p.plan ?? "-"}</TableCell>
                            <TableCell>
                              {p.start_date && p.end_date
                                ? `${p.start_date} → ${p.end_date}`
                                : "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <Divider sx={{ mt: 2, mb: 1 }} />
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </Fragment>
            )}
          </ContainerCard>
        </Grid>
      </Grid>
    </Fragment>
  );
}
